import { Collapse } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import type { LazyTableProps, MRT_ColumnDef } from '@vrplatform/table';
import { formatCurrency, sum, utc } from '@vrplatform/utils';
import { Avatar, Box, Center, Group, Text, rem } from '@mantine/core';
import { useMemo } from 'react';
import type { Reservation } from '../_queries';
import { DrawerCollapsableTable } from '../../_components';
import { LazyTable } from '@vrplatform/table/src/LazyTable';
import { ReservationSectionJumpTo } from './ReservationSectionJumpTo';
import { RESERVATION_ENTITY_SECTION_ROW_LIMIT } from '../_utils/reservation-entity-section-row-limit';
import { useDepositDetailDrawer } from '../../deposit-drawer';

export const ReservationDepositsSection = ({
  deposits,
  currency,
  goToTab,
  type,
}: {
  deposits: Reservation['deposits'];
  currency: Reservation['currency'];
  goToTab: () => void;
  type: 'tab' | 'collapse';
}) => {
  const { open: openDeposit } = useDepositDetailDrawer();

  const total =
    sum(
      deposits.filter((x) => x.status === 'active'),
      'centTotal'
    ) / 100;

  const columns = useMemo<MRT_ColumnDef<Reservation['deposits'][0]>[]>(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
        Cell: ({ row }) => {
          const deposit = row.original;

          const td = deposit.status === 'inactive' ? 'line-through' : undefined;

          return (
            <Box>
              <Text lineClamp={2} td={td}>
                {deposit.description}
              </Text>
              <Text c="gray" size="xs" td={td}>
                {deposit.date ? utc(deposit.date).format('LL') : ''}
              </Text>
            </Box>
          );
        },
      },
      {
        header: 'Badge',
        maxSize: 0,
        minSize: 120,
        size: 120,
        Cell: ({ row }) => {
          const connection = row.original.connection;

          return (
            <Group
              wrap="nowrap"
              gap={5}
              sx={(theme) => ({
                backgroundColor: theme.colors.gray[1],
                border: `1px solid ${theme.colors.gray[2]}`,
                paddingInline: rem(6),
                paddingBlock: rem(3),
                borderRadius: theme.radius.xl,
              })}
            >
              {connection?.app?.icon ? (
                <Avatar
                  size={16}
                  radius="xl"
                  mr={rem(3)}
                  src={connection.app.icon}
                />
              ) : (
                <Icon icon="PlusCircleIcon" size={14} />
              )}
              <Text span size="xs">
                {connection?.app?.id === 'csvImport'
                  ? 'CSV'
                  : connection?.app?.name || 'Manual'}
              </Text>
            </Group>
          );
        },
      },
      {
        header: 'amount',
        accessorKey: 'centTotal',
        maxSize: 0,
        minSize: 90,
        size: 90,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          const td =
            row.original.status === 'inactive' ? 'line-through' : undefined;

          return (
            <Text ta="right" td={td}>
              {formatCurrency(row.original.centTotal / 100, currency)}
            </Text>
          );
        },
      },
    ],
    [currency]
  );

  const title = 'Payments / Refunds';

  const onRowClick: LazyTableProps<
    Reservation['deposits'][0]
  >['table']['onRowClick'] = {
    handler: (row) => {
      if (row.original.id) openDeposit(row.original.id, 'push');
    },
    disabled: (row) => !row.original.id,
  };

  const rightSection = (
    <Text size="sm" fw={500}>
      {formatCurrency(total, currency)}
    </Text>
  );

  if (type === 'tab') {
    return (
      <>
        <Collapse
          title={
            <Text component="span" size="sm" fw={500}>
              {title}
              <Text c={'gray'} ml="xs" size="xs" component="span">
                {deposits.length}
              </Text>
            </Text>
          }
          rightSection={rightSection}
          hideToggle
        />
        <LazyTable
          data={{
            rows: deposits,
            error: null,
            loading: false,
            rowCount: deposits.length,
          }}
          table={{
            columns,
            hideTopBar: true,
            hideHeader: true,
            enableStickyFooter: true,
          }}
        />
      </>
    );
  }

  if (deposits.length > RESERVATION_ENTITY_SECTION_ROW_LIMIT)
    return (
      <ReservationSectionJumpTo
        title={title}
        count={deposits.length}
        goToTab={goToTab}
      />
    );

  return (
    <DrawerCollapsableTable
      title={title}
      rightSection={rightSection}
      rowData={deposits}
      columns={columns}
      onRowClick={onRowClick}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No payments / refunds found
          </Text>
        </Center>
      )}
    />
  );
};
